import Text from '../../Text';
import type { LabelHTMLAttributes, ReactNode } from 'react';
import { css } from '@mildang/styled-system/css';
import { Flex } from '@mildang/styled-system/jsx';
import type { PandaSafeHtmlProps } from '../../../types/HTMLStyledProps';

export interface FormLabelProps extends PandaSafeHtmlProps<LabelHTMLAttributes<HTMLLabelElement>> {
  /** 라벨 텍스트. */
  children?: ReactNode;
  /** 필수 입력 표시(*) 노출 여부. */
  required?: boolean;
  /**
   * 라벨 아래에 표시되는 보조 설명. Figma form label의 Description 레이어에 대응.
   * caption-lg / neutral.text.lowest(=gray.light.1000 #6b7180)로 Title 아래에 렌더됨.
   */
  description?: ReactNode;
}

export const FormLabel = (props: FormLabelProps) => {
  const { children, required = false, description, ...other } = props;

  const asterisk = required ? (
    <abbr
      title="required"
      className={css({
        ml: '2',
        textStyle: 'title-sm-medium',
        color: 'critical.text.base',
        textDecoration: 'none',
        borderBottom: 'none',
        // Pretendard 기본 '*' 글리프는 윗선(superscript) 위치라 라벨 텍스트와 어긋난다.
        // 'case'(Case-Sensitive Forms)가 구두점/별표를 대문자 기준 가운데로 정렬해준다.
        fontFeatureSettings: "'case' 1",
      })}
    >
      *
    </abbr>
  ) : null;

  const label = (
    <Text as="label" variant="title-sm-medium" {...other}>
      {children}
      {asterisk}
    </Text>
  );

  // description 없으면 기존과 동일하게 label 하나만 렌더(하위 호환).
  // ReactNode 라 falsy 검사를 쓰면 `0` 이 사라진다.
  if (description == null) {
    return label;
  }

  // description이 있으면 Title(+별표) 위, Description 아래로 세로 배치.
  // Figma form_title_item은 itemSpacing=0 — Title/Description은 각자의 lineHeight(24/22px)로만
  // 간격을 두고 레이아웃 gap은 없다. (gap을 주면 시안보다 벌어짐)
  return (
    <Flex direction="column">
      {label}
      <Text as="span" variant="caption-lg" color="neutral.text.lowest">
        {description}
      </Text>
    </Flex>
  );
};
